import { Checkbox, Spinner, Text } from "@portal/components/primitives";
import { GlobalSearchFilterTabs } from "@portal/components/ui/GlobalSearch/GlobalSearchFilterTabs";
import { GlobalSearchResult } from "@portal/components/ui/GlobalSearch/GlobalSearchResult";
import { GlobalSearchResultsCategory } from "@portal/components/ui/GlobalSearch/GlobalSearchResultsCategory";
import { type ActiveFilter, ActiveFilterEnum } from "@portal/components/ui/GlobalSearch/types";
import { useGlobalSearchData } from "@portal/components/ui/GlobalSearch/useGlobalSearchData";
import { SearchInput } from "@portal/components/ui/SearchInput";
import { useSearchAnalytics } from "@portal/hooks/useSearchAnalytics";
import { useState } from "react";
import { noop } from "../../../../utils";

export const DashboardSearchPanel = () => {
	const [searchTerm, setSearchTerm] = useState("");
	const [activeFilter, setActiveFilter] = useState<ActiveFilter>(ActiveFilterEnum.ALL);
	const [showClosed, setShowClosed] = useState(false);
	const { onSearchTermChange, onResultClicked } = useSearchAnalytics("dashboard");

	const {
		results: { recentlyViewed, accounts, bonds, bondRequests },
		isLoading,
	} = useGlobalSearchData({ searchTerm, activeFilter, showClosed });

	const isAllWithSearch = activeFilter === ActiveFilterEnum.ALL && searchTerm !== "";
	const showRecentlyViewed = activeFilter === ActiveFilterEnum.ALL && searchTerm === "";
	const resolvedSearchTerm = searchTerm === "" ? undefined : searchTerm;

	const handleSearchTermChange = (value: string) => {
		setSearchTerm(value);
		onSearchTermChange(value);
	};

	return (
		<div
			data-testid="dashboard-search-panel"
			className="flex w-full flex-col rounded-lg border-2 border-bc-strong bg-surface-base shadow-sm"
		>
			<div className="flex flex-col gap-3 border-b border-bc-default p-4">
				<div className="flex flex-1 items-center gap-3 rounded-lg bg-surface-light pr-3 shadow-sm outline outline-2 outline-offset-[-1px] outline-bc-strong transition-colors focus-within:bg-surface-base focus-within:outline-brand-accent">
					<SearchInput
						testId="dashboard-search-input"
						placeholder="Search accounts, bond requests, and bonds..."
						onChange={handleSearchTermChange}
						className="h-[52px] flex-1 rounded-lg bg-transparent"
						inputClassName="flex-1 text-base text-primary placeholder:text-base placeholder:text-secondary"
						hideFocusStyles
					/>
				</div>
				<div className="flex flex-1 flex-wrap items-center justify-between gap-x-4 gap-y-2">
					<GlobalSearchFilterTabs
						activeFilter={activeFilter}
						onChange={setActiveFilter}
						className="flex-wrap"
					/>
					<div
						data-testid="dashboard-search-show-closed"
						className="flex shrink-0 items-center justify-end"
					>
						<Checkbox
							checked={showClosed}
							onChange={setShowClosed}
							label={
								<Text type="body" className="whitespace-nowrap">
									Show closed
								</Text>
							}
						/>
					</div>
				</div>
			</div>

			{isLoading ? (
				<div className="flex w-full items-center justify-center py-12">
					<div data-testid="dashboard-search-loading">
						<Spinner />
					</div>
				</div>
			) : (
				<div className="flex w-full flex-col items-start">
					{showRecentlyViewed && (
						<>
							<Text type="caption" color="secondary" className="p-2">
								Recently viewed
							</Text>
							<div className="mb-2 flex w-full items-center justify-center">
								{!recentlyViewed || recentlyViewed.length === 0 ? (
									<Text type="body" color="tertiary" className="pb-4">
										No recently viewed
									</Text>
								) : (
									<div className="flex w-full flex-col overflow-y-auto text-xs">
										{recentlyViewed.map((result, index) => (
											<GlobalSearchResult
												key={`${index}-${result.relationType}-${result.entity.id}`}
												{...result}
												resultSource="recentlyViewed"
												onResultClicked={onResultClicked}
												onNavigate={noop}
											/>
										))}
									</div>
								)}
							</div>
						</>
					)}
					<div className="w-full max-h-[450px] overflow-y-auto scrollbar-gutter-stable scrollbar-med">
						{((isAllWithSearch && (accounts?.length ?? 0) > 0) ||
							activeFilter === ActiveFilterEnum.ACCOUNTS) && (
							<GlobalSearchResultsCategory
								category={ActiveFilterEnum.ACCOUNTS}
								results={accounts}
								searchTerm={resolvedSearchTerm}
								selfScroll={false}
								onResultClicked={onResultClicked}
								onNavigate={noop}
							/>
						)}
						{((isAllWithSearch && (bondRequests?.length ?? 0) > 0) ||
							activeFilter === ActiveFilterEnum.REQUESTS) && (
							<GlobalSearchResultsCategory
								category={ActiveFilterEnum.REQUESTS}
								results={bondRequests}
								searchTerm={resolvedSearchTerm}
								selfScroll={false}
								onResultClicked={onResultClicked}
								onNavigate={noop}
							/>
						)}
						{((isAllWithSearch && (bonds?.length ?? 0) > 0) ||
							activeFilter === ActiveFilterEnum.BONDS) && (
							<GlobalSearchResultsCategory
								category={ActiveFilterEnum.BONDS}
								results={bonds}
								searchTerm={resolvedSearchTerm}
								selfScroll={false}
								onResultClicked={onResultClicked}
								onNavigate={noop}
							/>
						)}
						{isAllWithSearch &&
							(accounts?.length ?? 0) === 0 &&
							(bondRequests?.length ?? 0) === 0 &&
							(bonds?.length ?? 0) === 0 && (
								<div className="mt-4 w-full px-4 pb-4 text-center">
									<Text data-testid="dashboard-search-no-results" type="caption" color="tertiary">
										No results for "{searchTerm}"
									</Text>
								</div>
							)}
					</div>
				</div>
			)}
		</div>
	);
};
